import { useState, useEffect, useCallback, FC } from 'react';
import {
  Link2,
  Plus,
  Copy,
  Check,
  ExternalLink,
  Edit2,
  Trash2,
  Power,
  LayoutGrid,
  List,
  MousePointerClick,
  Eye,
  Percent,
  Sparkles,
  Palette,
} from 'lucide-react';

const COLOR_PRESETS = [
  { label: 'Verde WhatsApp', value: '#10B981' },
  { label: 'Azul Real', value: '#2563EB' },
  { label: 'Âmbar Dourado', value: '#F59E0B' },
  { label: 'Vermelho Intenso', value: '#EF4444' },
  { label: 'Índigo Violeta', value: '#8B5CF6' },
];

const THEME_PRESETS = [
  { label: 'Escuro Padrão (Dark)', value: 'dark' },
  { label: 'Claro Minimalista (Light)', value: 'light' },
  { label: 'Esmeralda Nobre (Emerald)', value: 'emerald' },
  { label: 'Índigo Tecnológico (Indigo)', value: 'indigo' },
  { label: 'Ardósia Grafite (Slate)', value: 'slate' },
];
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { Drawer } from '../ui/Drawer';
import { Tooltip } from '../ui/Tooltip';
import { PageHeader } from '../widgets/PageHeader';
import { MetricWidget } from '../widgets/MetricWidget';
import { DataTable, Column } from '../widgets/DataTable';
import {
  BridgePage,
  BridgePageCreatePayload,
  fetchBridgePages,
  createBridgePage,
  updateBridgePage,
  deleteBridgePage,
  toggleBridgePageStatus,
} from '../../api/bridgePages';
import { fetchGroups, WhatsAppGroup } from '../../api/groups';
import { getAffiliateSettings } from '../../utils/affiliateSettings';

export interface BridgePagesViewProps {
  initialDrawerOpen?: boolean;
  initialPages?: BridgePage[];
}

export const BridgePagesView: FC<BridgePagesViewProps> = ({
  initialDrawerOpen = false,
  initialPages = [],
}) => {
  const { token } = useAuth();
  const { success, error: showError } = useToast();

  const [pages, setPages] = useState<BridgePage[]>(initialPages);
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

  // Drawer form state (Create / Edit)
  const [isDrawerOpen, setIsDrawerOpen] = useState(initialDrawerOpen);
  const [editingPage, setEditingPage] = useState<BridgePage | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [headline, setHeadline] = useState('');
  const [subheadline, setSubheadline] = useState('');
  const [buttonText, setButtonText] = useState('ENTRAR NO GRUPO VIP AGORA');
  const [buttonColor, setButtonColor] = useState('#10B981');
  const [backgroundTheme, setBackgroundTheme] = useState('dark');
  const [metaCapiToken, setMetaCapiToken] = useState('');
  const [timerSeconds, setTimerSeconds] = useState(180);
  const [metaPixelId, setMetaPixelId] = useState('');
  const [targetGroupId, setTargetGroupId] = useState('');
  const [showShowcaseLink, setShowShowcaseLink] = useState(true);
  const [themePreference, setThemePreference] = useState<
    'system' | 'light' | 'dark'
  >('system');
  const [isActive, setIsActive] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Delete modal state
  const [deletingPage, setDeletingPage] = useState<BridgePage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Copy link feedback
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const [pagesData, groupsData] = await Promise.all([
        fetchBridgePages(token),
        fetchGroups(token).catch(() => []),
      ]);
      setPages(pagesData);
      setGroups(groupsData);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showError(msg, 'Erro ao carregar páginas ponte');
    } finally {
      setIsLoading(false);
    }
  }, [token, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setTitle('');
    setSlug('');
    setHeadline('');
    setSubheadline(
      'Grupo exclusivo no WhatsApp com vagas limitadas. Transbordo automático ativo.'
    );
    setButtonText('ENTRAR NO GRUPO VIP AGORA');
    setButtonColor('#10B981');
    setBackgroundTheme('dark');
    setMetaCapiToken('');
    setTimerSeconds(180);
    setMetaPixelId('');
    setTargetGroupId('');
    setShowShowcaseLink(true);
    setThemePreference('system');
    setIsActive(true);
    setFormErrors({});
    setEditingPage(null);
  };

  const openCreateDrawer = () => {
    resetForm();
    setIsDrawerOpen(true);
  };

  const openEditDrawer = (page: BridgePage) => {
    setEditingPage(page);
    setTitle(page.title);
    setSlug(page.slug);
    setHeadline(page.headline_text || page.headline);
    setSubheadline(page.subheadline || '');
    setButtonText(page.button_text);
    setButtonColor(page.button_color || '#10B981');
    setBackgroundTheme(page.background_theme || 'dark');
    setMetaCapiToken(page.meta_capi_token || '');
    setTimerSeconds(page.timer_seconds);
    setMetaPixelId(page.meta_pixel_id || '');
    setTargetGroupId(page.target_group_id || '');
    setShowShowcaseLink(page.show_showcase_link ?? true);
    setThemePreference(page.theme_preference || 'system');
    setIsActive(page.is_active);
    setFormErrors({});
    setIsDrawerOpen(true);
  };

  const handleSlugChange = (val: string) => {
    const formatted = val
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-/, '');
    setSlug(formatted);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!title.trim()) errors.title = 'O título da campanha é obrigatório.';
    if (!slug.trim()) {
      errors.slug = 'O slug da URL é obrigatório.';
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim())) {
      errors.slug =
        'O slug deve conter apenas letras minúsculas, números e hífens (ex: eletronicos-vip).';
    }
    if (!headline.trim())
      errors.headline = 'A headline principal é obrigatória.';
    if (timerSeconds < 0 || timerSeconds > 86400) {
      errors.timerSeconds = 'O contador deve estar entre 0 e 86.400 segundos.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePage = async () => {
    if (!token || !validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload: BridgePageCreatePayload = {
        title: title.trim(),
        slug: slug.trim(),
        headline: headline.trim(),
        headline_text: headline.trim(),
        subheadline: subheadline.trim() || undefined,
        button_text: buttonText.trim() || 'ENTRAR NO GRUPO VIP AGORA',
        button_color: buttonColor.trim() || '#10B981',
        background_theme: backgroundTheme,
        meta_capi_token: metaCapiToken.trim() || undefined,
        timer_seconds: Number(timerSeconds),
        meta_pixel_id: metaPixelId.trim() || undefined,
        target_group_id: targetGroupId || null,
        show_showcase_link: showShowcaseLink,
        theme_preference: themePreference,
        is_active: isActive,
      };

      if (editingPage) {
        await updateBridgePage(token, editingPage.id, payload);
        success(`Página "/p/${slug}" atualizada com sucesso.`, 'Página Salva');
      } else {
        await createBridgePage(token, payload);
        success(`Página "/p/${slug}" criada com sucesso.`, 'Página Criada');
      }

      setIsDrawerOpen(false);
      resetForm();
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showError(msg, 'Falha ao salvar página ponte');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (page: BridgePage) => {
    if (!token) return;
    try {
      const updated = await toggleBridgePageStatus(token, page.id);
      success(
        `Página "/p/${updated.slug}" agora está ${updated.is_active ? 'Ativa' : 'Pausada'}.`,
        'Status Atualizado'
      );
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showError(msg, 'Erro ao alternar status');
    }
  };

  const handleDeletePage = async () => {
    if (!token || !deletingPage) return;
    setIsDeleting(true);
    try {
      await deleteBridgePage(token, deletingPage.id);
      success(`Página "/p/${deletingPage.slug}" excluída.`, 'Página Removida');
      setDeletingPage(null);
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showError(msg, 'Erro ao excluir página');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyLink = (pageSlug: string) => {
    const origin = window.location.origin;
    const url = `${origin}/p/${pageSlug}`;
    navigator.clipboard.writeText(url);
    setCopiedSlug(pageSlug);
    success(
      `Link ${url} copiado para a área de transferência!`,
      'Link Copiado'
    );
    setTimeout(() => setCopiedSlug(null), 2500);
  };

  // Métricas agregadas
  const totalViews = pages.reduce((acc, p) => acc + p.total_views, 0);
  const totalConversions = pages.reduce(
    (acc, p) => acc + p.total_conversions,
    0
  );
  const avgConversionRate =
    totalViews > 0 ? ((totalConversions / totalViews) * 100).toFixed(1) : '0.0';

  // Configuração das Colunas do DataTable
  const columns: Column<BridgePage>[] = [
    {
      key: 'title',
      header: 'Página Ponte & Slug',
      render: (item) => (
        <div className="min-w-0 max-w-xs space-y-1">
          <Tooltip
            content={item.title}
            position="bottom"
            className="block w-full min-w-0"
          >
            <span className="block truncate text-sm font-semibold text-content-main">
              {item.title}
            </span>
          </Tooltip>
          <div className="flex items-center gap-1.5 font-mono text-2xs text-brand-600 dark:text-brand-400">
            <span className="rounded bg-brand-500/10 px-1.5 py-0.5">
              /p/{item.slug}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      className: 'w-28',
      render: (item) => (
        <Badge
          variant={item.is_active ? 'online' : 'neutral'}
          withDot
          pulse={item.is_active}
        >
          {item.is_active ? 'Ativa' : 'Pausada'}
        </Badge>
      ),
    },
    {
      key: 'target_group',
      header: 'Direcionamento',
      render: (item) => (
        <div className="space-y-0.5">
          <span className="block truncate text-xs font-medium text-content-main">
            {item.target_group_name || 'Transbordo Inteligente Geral'}
          </span>
          <span className="block text-micro text-content-muted">
            {item.target_group_name
              ? 'Grupo fixo selecionado'
              : 'Roteamento automático com vagas livres'}
          </span>
        </div>
      ),
    },
    {
      key: 'views',
      header: 'Visualizações',
      className: 'text-center w-28',
      render: (item) => (
        <span className="font-mono text-xs font-semibold text-content-main">
          {item.total_views}
        </span>
      ),
    },
    {
      key: 'conversions',
      header: 'Conversão',
      className: 'text-center w-36',
      render: (item) => (
        <div className="space-y-0.5 text-center">
          <span className="font-mono text-xs font-bold text-brand-600 dark:text-brand-400">
            {item.conversion_rate}%
          </span>
          <span className="block font-mono text-micro text-content-muted">
            {item.total_conversions} leads
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      className: 'text-right w-44',
      render: (item) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleCopyLink(item.slug)}
            title="Copiar link público da página ponte"
          >
            {copiedSlug === item.slug ? (
              <Check className="h-3.5 w-3.5 text-brand-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
          <a
            href={`/p/${item.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-subtle hover:text-content-main"
            title="Abrir página ponte"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleToggleStatus(item)}
            title={item.is_active ? 'Pausar página' : 'Ativar página'}
          >
            <Power
              className={`h-3.5 w-3.5 ${item.is_active ? 'text-brand-600 dark:text-brand-400' : 'text-content-muted'}`}
            />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDrawer(item)}
            title="Editar configurações"
          >
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeletingPage(item)}
            title="Excluir página ponte"
            className="text-content-muted hover:text-red-600 dark:hover:text-red-400"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* 1. PageHeader Estrutural do Design System */}
      <PageHeader
        title="Páginas Ponte"
        subtitle="Landing pages dinâmicas para captação de tráfego pago e orgânico com transbordo inteligente"
        actions={
          <div className="flex items-center gap-2">
            <a
              href={`/showcase/${getAffiliateSettings().tenantSlug || 'default'}`}
              target="_blank"
              rel="noreferrer"
              className="hover:bg-surface-border/50 inline-flex items-center gap-1.5 rounded-xl border border-surface-border bg-surface-subtle px-3 py-1.5 text-xs font-semibold text-content-main transition-colors"
            >
              <span>Ver Vitrine / Lojinha</span>
              <ExternalLink className="h-3.5 w-3.5 text-content-muted" />
            </a>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={openCreateDrawer}
            >
              Nova Página Ponte
            </Button>
          </div>
        }
      />

      {/* 2. Métricas e Telemetria de Captação */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        <MetricWidget
          label="Páginas Cadastradas"
          value={pages.length}
          icon={Link2}
        />
        <MetricWidget
          label="Visualizações Totais"
          value={totalViews}
          icon={Eye}
        />
        <MetricWidget
          label="Conversões (Leads)"
          value={totalConversions}
          icon={MousePointerClick}
        />
        <MetricWidget
          label="Taxa de Conversão"
          value={`${avgConversionRate}%`}
          icon={Percent}
        />
      </div>

      {/* 3. Tabela & Grid de Páginas */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-main">
            Campanhas Ativas ({pages.length})
          </h3>

          <div className="flex items-center gap-1 rounded-xl border border-surface-border bg-surface-subtle p-1">
            <button
              onClick={() => setViewMode('table')}
              title="Visualização em Tabela"
              className={`cursor-pointer rounded-lg p-1.5 text-xs transition-colors ${
                viewMode === 'table'
                  ? 'shadow-xs bg-surface-panel font-medium text-content-main'
                  : 'text-content-muted hover:text-content-main'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              title="Visualização em Cards"
              className={`cursor-pointer rounded-lg p-1.5 text-xs transition-colors ${
                viewMode === 'cards'
                  ? 'shadow-xs bg-surface-panel font-medium text-content-main'
                  : 'text-content-muted hover:text-content-main'
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Estado Vazio */}
        {pages.length === 0 && !isLoading ? (
          <Card>
            <Card.Body className="mx-auto max-w-md space-y-4 px-4 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-brand-500/20 bg-brand-500/10 text-brand-600 dark:text-brand-400">
                <Link2 className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-content-main">
                  Nenhuma Página Ponte Criada
                </h4>
                <p className="text-xs leading-relaxed text-content-muted">
                  Crie sua primeira landing page otimizada com contagem
                  regressiva e encaminhamento automático para grupos com vagas.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={openCreateDrawer}
              >
                Criar Primeira Página Ponte
              </Button>
            </Card.Body>
          </Card>
        ) : viewMode === 'table' ? (
          <DataTable
            data={pages}
            columns={columns}
            keyExtractor={(item) => item.id}
          />
        ) : (
          /* Cards Grid */
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pages.map((page) => (
              <Card key={page.id} className="flex flex-col justify-between">
                <Card.Header
                  title={
                    <div className="min-w-0 space-y-1">
                      <Tooltip
                        content={page.title}
                        position="bottom"
                        className="block w-full min-w-0"
                      >
                        <span className="block truncate text-sm font-semibold text-content-main">
                          {page.title}
                        </span>
                      </Tooltip>
                      <span className="inline-block rounded font-mono text-2xs font-semibold text-brand-600 dark:text-brand-400">
                        /p/{page.slug}
                      </span>
                    </div>
                  }
                  action={
                    <Badge
                      variant={page.is_active ? 'online' : 'neutral'}
                      withDot
                      pulse={page.is_active}
                    >
                      {page.is_active ? 'Ativa' : 'Pausada'}
                    </Badge>
                  }
                />
                <Card.Body className="space-y-3">
                  <p className="line-clamp-2 rounded-lg border border-surface-border bg-surface-subtle p-2.5 text-xs text-content-muted">
                    {page.headline}
                  </p>

                  <div className="grid grid-cols-2 gap-2 text-2xs">
                    <div className="rounded-lg border border-surface-border bg-surface-subtle p-2">
                      <span className="block text-content-muted">
                        Direcionamento
                      </span>
                      <span className="block truncate font-medium text-content-main">
                        {page.target_group_name || 'Transbordo Dinâmico'}
                      </span>
                    </div>
                    <div className="rounded-lg border border-surface-border bg-surface-subtle p-2">
                      <span className="block text-content-muted">
                        Conversão
                      </span>
                      <span className="font-mono font-bold text-brand-600 dark:text-brand-400">
                        {page.conversion_rate}% ({page.total_conversions} leads)
                      </span>
                    </div>
                  </div>
                </Card.Body>
                <Card.Footer className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyLink(page.slug)}
                    leftIcon={
                      copiedSlug === page.slug ? (
                        <Check className="h-3.5 w-3.5 text-brand-500" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )
                    }
                  >
                    {copiedSlug === page.slug ? 'Copiado!' : 'Copiar Link'}
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleStatus(page)}
                      title={page.is_active ? 'Pausar' : 'Ativar'}
                    >
                      <Power
                        className={`h-3.5 w-3.5 ${page.is_active ? 'text-brand-600 dark:text-brand-400' : 'text-content-muted'}`}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDrawer(page)}
                      title="Editar"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeletingPage(page)}
                      title="Excluir"
                      className="text-content-muted hover:text-red-600 dark:hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* 4. Drawer Lateral Deslizante (Criação e Edição de Página Ponte) */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={
          editingPage ? 'Editar Página Ponte' : 'Nova Página Ponte de Captura'
        }
        description="Configure sua landing page com gatilhos de urgência e roteamento automático para grupos do WhatsApp."
        width="md"
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsDrawerOpen(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={isSubmitting}
              onClick={handleSavePage}
            >
              {editingPage ? 'Salvar Alterações' : 'Criar Página Ponte'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Herança Automática de Identidade do Tenant */}
          <div className="flex items-start gap-2 rounded-xl border border-brand-500/20 bg-brand-500/10 p-3 text-xs text-brand-700 dark:text-brand-300">
            <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <p className="font-semibold">Herança Visual Ativa</p>
              <p className="mt-0.5 leading-relaxed text-content-muted">
                Esta página herdará automaticamente as cores da sua marca,
                logotipo e pixels globais configurados na aba Configurações.
              </p>
            </div>
          </div>

          <Input
            label="Nome Interno da Campanha"
            required
            placeholder="Ex: Campanha Eletrônicos Black VIP"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={formErrors.title}
          />

          <div className="space-y-1.5">
            <Input
              label="Slug da URL Pública (/p/:slug)"
              required
              placeholder="eletronicos-vip"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              error={formErrors.slug}
            />
            {slug && (
              <p className="font-mono text-2xs text-content-muted">
                Link público:{' '}
                <span className="font-semibold text-brand-600 dark:text-brand-400">
                  {typeof window !== 'undefined' ? window.location.origin : ''}
                  /p/{slug}
                </span>
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-content-main">
              Headline Principal <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              rows={2}
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="🔥 Ofertas Secretas de Eletrônicos com até 80% OFF!"
              className="w-full rounded-xl border border-surface-border bg-surface-subtle px-3 py-2 text-xs text-content-main transition-colors focus:border-brand-500 focus:outline-none"
            />
            {formErrors.headline && (
              <p className="text-micro text-red-500">{formErrors.headline}</p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-content-main">
              Subheadline / Texto de Apoio
            </label>
            <textarea
              rows={2}
              value={subheadline}
              onChange={(e) => setSubheadline(e.target.value)}
              placeholder="Grupo exclusivo no WhatsApp com vagas limitadas. Transbordo automático ativo."
              className="w-full rounded-xl border border-surface-border bg-surface-subtle px-3 py-2 text-xs text-content-main transition-colors focus:border-brand-500 focus:outline-none"
            />
          </div>

          <Input
            label="Texto do Botão CTA"
            placeholder="ENTRAR NO GRUPO VIP AGORA"
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
          />

          {/* Paleta de Cores e Personalização do Botão CTA */}
          <div className="space-y-2 rounded-xl border border-surface-border bg-surface-subtle p-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-content-main">
                <Palette className="h-3.5 w-3.5 text-brand-500" />
                Cor do Botão de Chamada para Ação
              </label>
              <span className="flex items-center gap-1.5 font-mono text-2xs text-content-muted">
                <span
                  className="inline-block h-3 w-3 rounded-full border border-surface-border"
                  style={{ backgroundColor: buttonColor }}
                />
                {buttonColor}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setButtonColor(preset.value)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-2xs font-medium transition-all ${
                    buttonColor.toLowerCase() === preset.value.toLowerCase()
                      ? 'border-brand-500 bg-brand-500/10 font-bold text-brand-600 dark:text-brand-400'
                      : 'border-surface-border bg-surface-panel text-content-muted hover:text-content-main'
                  }`}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: preset.value }}
                  />
                  {preset.label}
                </button>
              ))}
            </div>
            <Input
              label="Código Hexadecimal da Cor"
              placeholder="#10B981"
              value={buttonColor}
              onChange={(e) => setButtonColor(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-content-main">
                Tema de Fundo da Página Ponte
              </label>
              <select
                value={backgroundTheme}
                onChange={(e) => setBackgroundTheme(e.target.value)}
                className="w-full rounded-xl border border-surface-border bg-surface-subtle px-3 py-2 text-xs text-content-main transition-colors focus:border-brand-500 focus:outline-none"
              >
                {THEME_PRESETS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Contador Regressivo (Segundos)"
              type="number"
              min={0}
              max={86400}
              value={timerSeconds}
              onChange={(e) => setTimerSeconds(Number(e.target.value))}
              error={formErrors.timerSeconds}
            />
          </div>

          <div className="space-y-3 rounded-xl border border-surface-border bg-surface-subtle p-3">
            <p className="text-xs font-semibold text-content-main">
              Rastreamento & Conversões da Meta (Pixel & CAPI)
            </p>
            <Input
              label="Meta Pixel ID Customizado (Opcional)"
              placeholder="Ex: 123456789012345"
              value={metaPixelId}
              onChange={(e) => setMetaPixelId(e.target.value)}
            />
            <div className="space-y-1">
              <Input
                label="Meta CAPI Access Token (Server-Side)"
                type="password"
                placeholder="EAAX..."
                value={metaCapiToken}
                onChange={(e) => setMetaCapiToken(e.target.value)}
              />
              <p className="text-micro text-content-muted">
                Dispara o evento Lead server-side capturando IP e User-Agent via
                Meta Graph API para 100% de atribuição.
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-content-main">
              Grupo de WhatsApp de Destino
            </label>
            <select
              value={targetGroupId}
              onChange={(e) => setTargetGroupId(e.target.value)}
              className="w-full rounded-xl border border-surface-border bg-surface-subtle px-3 py-2 text-xs text-content-main transition-colors focus:border-brand-500 focus:outline-none"
            >
              <option value="">
                Transbordo Inteligente Geral (Recomendado)
              </option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} ({g.current_members}/{g.max_capacity} membros) -{' '}
                  {g.status === 'active' ? 'Ativo' : g.status}
                </option>
              ))}
            </select>
            <p className="text-micro text-content-muted">
              Ao selecionar Transbordo Inteligente Geral, o visitante é
              encaminhado para o grupo prioritário com vagas livres no momento.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-content-main">
                Preferencia de Tema do Sistema
              </label>
              <select
                value={themePreference}
                onChange={(e) =>
                  setThemePreference(
                    e.target.value as 'system' | 'light' | 'dark'
                  )
                }
                className="w-full rounded-xl border border-surface-border bg-surface-subtle px-3 py-2 text-xs text-content-main transition-colors focus:border-brand-500 focus:outline-none"
              >
                <option value="system">Automático (Segue o Sistema)</option>
                <option value="light">Tema Claro (Light)</option>
                <option value="dark">Tema Escuro (Dark)</option>
              </select>
            </div>

            <div className="flex items-center gap-2 rounded-xl border border-surface-border bg-surface-subtle p-3">
              <input
                type="checkbox"
                id="show_showcase_link_toggle"
                checked={showShowcaseLink}
                onChange={(e) => setShowShowcaseLink(e.target.checked)}
                className="h-4 w-4 rounded border-surface-border text-brand-500 focus:ring-brand-500"
              />
              <label
                htmlFor="show_showcase_link_toggle"
                className="cursor-pointer text-xs font-medium text-content-main"
              >
                Exibir link para vitrine pública de ofertas
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-surface-border bg-surface-subtle p-3">
            <input
              type="checkbox"
              id="is_active_toggle"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-surface-border text-brand-500 focus:ring-brand-500"
            />
            <label
              htmlFor="is_active_toggle"
              className="cursor-pointer text-xs font-medium text-content-main"
            >
              Página ativa para recebimento de visitantes
            </label>
          </div>
        </div>
      </Drawer>

      {/* 5. Modal de Confirmação para Exclusão de Página */}
      <ConfirmationModal
        isOpen={Boolean(deletingPage)}
        onClose={() => setDeletingPage(null)}
        onConfirm={handleDeletePage}
        title="Excluir Página Ponte?"
        description={`Tem certeza que deseja excluir a página "/p/${deletingPage?.slug}"? Links existentes em anúncios ou redes sociais deixarão de funcionar imediatamente.`}
        confirmText="Excluir Página"
        cancelText="Cancelar"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};
